import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { useToast } from "./Toast";
import { allColumns, applyPins, cellText } from "../lib/columns";
import { useColumnFilters } from "../lib/columnFilters";
import { useColumnPins } from "../lib/columnPins";
import { useFrozenOffsets } from "../lib/frozenOffsets";
import { sortPositions, type SortSpec } from "../lib/ledgerView";
import { useGridSelection } from "./useGridSelection";
import { useColumnWidths } from "./useColumnWidths";
import FullscreenTable from "./FullscreenTable";
import { HeadControls } from "./GridControls";
import type { ReconResult, ManualMatchPair, SideDisplayConfig } from "../types/api";

interface Props {
  result: ReconResult;
  prematchId: string;
  insurerName: string;
  sessionPairs: ManualMatchPair[];
  notesCri: Record<string, string>;
  notesInsurer: Record<string, string>;
  // null: the session was re-run underneath and the page already holds the fresh result.
  onApplied: (result: ReconResult | null, notesCri: Record<string, string>, notesInsurer: Record<string, string>) => void;
  // Re-runs the reconciliation after the server's saved session expired; resolves the
  // new prematch id to retry against.
  onPrematchExpired?: () => Promise<string>;
}

const NONE = "(none)";

// Manual Notes tab — mirrors app/overrides.py::_render_manual_overrides_tab: a free-text
// note per row, math-inert (writes only the note column, never changes a total). Notes
// are keyed by the ORIGINAL row index (frame.indices), the same {row_index: text} dict
// stamp_manual_notes consumes, and applied via /finalize (no engine re-run).
export default function ManualNotesTab({ result, prematchId, insurerName, sessionPairs, notesCri, notesInsurer, onApplied, onPrematchExpired }: Props) {
  const toast = useToast();
  const [side, setSide] = useState<"cri" | "insurer">("cri");
  const [draftCri, setDraftCri] = useState<Record<string, string>>(notesCri);
  const [draftIns, setDraftIns] = useState<Record<string, string>>(notesInsurer);
  const [customPhrases, setCustomPhrases] = useState<string[]>([]);
  const [newPhrase, setNewPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE = 100;

  const suggestions = useMemo(
    () => [NONE, ...(result.note_suggestions ?? []), ...customPhrases],
    [result.note_suggestions, customPhrases]
  );

  const frame = side === "cri" ? result.df_cri : result.df_insurer;
  const cfg: SideDisplayConfig | undefined = side === "cri" ? result.display_config?.cri : result.display_config?.insurer;
  const draft = side === "cri" ? draftCri : draftIns;
  const setDraft = side === "cri" ? setDraftCri : setDraftIns;
  // ALL populated columns (curated order first) so any row is identifiable; the
  // table scrolls horizontally and the note column stays pinned on the right.
  // Pins share the statement grids' scope for this side, so a column pinned there is
  // pinned here too (the CRI scope is shared across insurers; insurer scope is per-insurer).
  const pins = useColumnPins(side === "cri" ? "cri" : "insurer", insurerName);
  const vis = useMemo(() => applyPins(allColumns(frame, cfg), pins.pinned), [frame, cfg, pins.pinned]);

  // Per-column header filters (same interaction as the statement grids). Applied over
  // the whole frame; the note draft stays keyed by the original frame index.
  const cf = useColumnFilters(frame);
  const [openCol, setOpenCol] = useState<string | null>(null);
  // File order until the user sorts.
  const [sort, setSort] = useState<SortSpec | null>(null);
  const frozen = useFrozenOffsets(pins.pinned, vis);
  const lastFrozen = useMemo(() => {
    let last: string | undefined;
    for (const c of vis) if (frozen.offsetFor(c.key) !== undefined) last = c.key;
    return last;
  }, [vis, frozen]);
  useEffect(() => { setOpenCol(null); setPage(0); setSort(null); }, [side, frame]);
  useEffect(() => { setPage(0); }, [cf.colFilters, sort]);

  // Filtered rows carried WITH their original index (the note key). Filtering runs
  // over the frame once; paging then slices — mapping all rows (~96k on Dubai/NIA)
  // per render would freeze the tab, so the filtered list is memoised.
  const rows = useMemo(() => {
    const out: { r: (typeof frame.rows)[number]; idx: string }[] = [];
    if (cf.nActive === 0) {
      for (let i = 0; i < frame.rows.length; i++) out.push({ r: frame.rows[i], idx: String(frame.indices[i] ?? i) });
    } else {
      const kept = cf.filterRows(frame.rows, frame.columns);
      const keptSet = new Set(kept);
      for (let i = 0; i < frame.rows.length; i++) {
        if (keptSet.has(frame.rows[i])) out.push({ r: frame.rows[i], idx: String(frame.indices[i] ?? i) });
      }
    }
    const order = sortPositions(frame, out.map((x) => x.r), sort);
    return order ? order.map((p) => out[p]) : out;
  }, [frame, cf, sort]);
  const pageRows = useMemo(() => rows.slice(page * PAGE, (page + 1) * PAGE), [rows, page]);
  const nPages = Math.max(1, Math.ceil(rows.length / PAGE));

  // Excel-style cell selection over the DATA columns only — the trailing Manual Note
  // dropdown is excluded, so dragging a selection can never hijack the select control.
  // Row coordinates are positions in `rows`, so a column selection foots every filtered
  // row rather than the page on screen.
  const selRows = useMemo(() => rows.map((x) => x.r), [rows]);
  const selIds = useMemo(() => rows.map((x) => x.idx), [rows]);
  const colIdx = useMemo(() => vis.map((c) => c.index), [vis]);
  const colLabels = useMemo(() => vis.map((c) => c.label), [vis]);
  const sel = useGridSelection(selRows, colIdx, colLabels, selIds);
  const cw = useColumnWidths(useMemo(() => [...vis.map((c) => c.key), "__note"], [vis]), frame);

  function setNote(idx: string, note: string) {
    setDraft((d) => {
      const next = { ...d };
      if (note === NONE || note === "") delete next[idx];
      else next[idx] = note;
      return next;
    });
  }

  function addPhrase() {
    const p = newPhrase.trim();
    if (p && !suggestions.includes(p)) setCustomPhrases((c) => [...c, p]);
    setNewPhrase("");
  }

  const [error, setError] = useState("");

  async function apply() {
    setBusy(true);
    setError("");
    try {
      // Notes are math-inert (they never move a figure), so the server stamps them
      // and we keep the CURRENT result — no need to re-download the big frames.
      const stamp = (pid: string) => api.finalize({
        prematchId: pid, insurerName, sessionPairs,
        manualNotesCri: draftCri, manualNotesInsurer: draftIns,
        includeFrames: false,
      });
      // The result on screen, unless the re-run below replaced it: this closure's copy
      // is then the expired run's, and handing it back would put its figures back up.
      let current: ReconResult | null = result;
      try {
        await stamp(prematchId);
      } catch (e) {
        // Saved session expired (409): re-run once and stamp against the fresh one.
        if (!(e instanceof ApiError && e.status === 409 && onPrematchExpired)) throw e;
        toast.info("Session data had expired — re-running the reconciliation, then saving your notes…");
        await stamp(await onPrematchExpired());
        current = null;
      }
      onApplied(current, draftCri, draftIns);
      const n = Object.keys(draftCri).length + Object.keys(draftIns).length;
      toast.success(n ? `${n} note${n === 1 ? "" : "s"} saved — they'll appear in the export.` : "Notes cleared.");
    } catch (e) {
      setError("Could not apply notes: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  const noteCount = Object.keys(draftCri).length + Object.keys(draftIns).length;

  return (
    <FullscreenTable label="Manual notes">
      {(scrollMax) => (
    <div>
      <p className="muted">
        Add a note to any row for the annotated export. Notes are export-only — they never change a total.
        Press Apply notes to save.
      </p>
      {error && <div className="badge badge-risk block-badge" role="alert">{error}</div>}

      <div className="notes-toolbar">
        <button className="btn btn-primary" onClick={apply} disabled={busy}>
          {busy ? "Applying…" : `Apply notes${noteCount ? ` (${noteCount})` : ""}`}
        </button>
        <input className="input grid-search--narrow" placeholder="New note phrase…" value={newPhrase}
          onChange={(e) => setNewPhrase(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPhrase()} />
        <button className="btn" onClick={addPhrase} disabled={!newPhrase.trim()}>Add phrase</button>
      </div>

      {/* Real tab semantics + roving tabindex, matching AdditionalDetails' strip. These
          were plain divs, so this was the one tab strip in the app a keyboard user
          could not operate. */}
      <div className="tabstrip tabstrip--inline" role="tablist" aria-label="Notes side">
        {([["cri", "CRI rows"], ["insurer", "Insurer rows"]] as const).map(([val, label]) => {
          const on = side === val;
          return (
            <button key={val} type="button" role="tab" aria-selected={on} tabIndex={on ? 0 : -1}
              className={`tab ${on ? "active" : ""}`}
              onClick={() => { setSide(val); setPage(0); }}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                  e.preventDefault();
                  setSide(val === "cri" ? "insurer" : "cri");
                  setPage(0);
                }
              }}>
              {label}
            </button>
          );
        })}
      </div>

      {cf.nActive > 0 && (
        <button className="btn btn-sm subtype-clear" onClick={cf.clearAll}>
          Clear {cf.nActive} filter{cf.nActive === 1 ? "" : "s"} · {rows.length.toLocaleString()} of {frame.rows.length.toLocaleString()} rows
        </button>
      )}

      <div {...sel.gridProps}>
      <div ref={(el) => { frozen.scrollRef.current = el; sel.scrollHostRef.current = el; }}
        className="grid-scroll" style={{ maxHeight: scrollMax ?? 560 }}>
        <table className={"data-table data-table--separate" + cw.className} style={cw.style}>
          {cw.colgroup}
          <thead>
            <tr>
              {vis.map((c, ci) => {
                const left = frozen.offsetFor(c.key);
                const cls = [
                  left !== undefined ? "pinned-col" + (c.key === lastFrozen ? " pinned-edge" : "") : "",
                  sel.isColSelected(ci) ? "col-selected" : "",
                ].filter(Boolean).join(" ");
                return (
                  <th key={c.key} data-col-key={c.key} className={`nowrap ${cls}`} style={{ left }}>
                    <span className="th-inner">
                      {/* Only the LABEL selects the column — filter and pin controls sit
                          outside it, so opening a filter never foots a column. */}
                      <span className="th-label" title="Click to total this column · Ctrl+click to add another"
                        onClick={(e) => { sel.onHeaderClick(ci, e); sel.focusGrid(); }}>
                        {c.label}
                      </span>
                      <HeadControls frame={frame} col={c}
                        filter={{
                          active: (cf.colFilters[c.key]?.length ?? 0) > 0,
                          open: openCol === c.key,
                          options: () => cf.distinctFor(c),
                          selected: cf.colFilters[c.key] ?? [],
                          onToggleOpen: () => setOpenCol(openCol === c.key ? null : c.key),
                          onChange: (v) => cf.setColFilter(c.key, v),
                        }}
                        sort={{ spec: sort, onSort: setSort }}
                        pin={{ pinned: pins.isPinned(c.key), onToggle: () => pins.toggle(c.key) }} />
                    </span>
                    {/* Autofit reads every filtered row, not just this page. */}
                    {cw.grip(c.key, () => rows.map((x) => cellText(x.r[c.index], c.key)))}
                  </th>
                );
              })}
              <th className="sticky-note-col note-col">Manual Note{cw.grip("__note")}</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(({ r, idx }, ri) => {
              const rowPos = page * PAGE + ri;  // position in `rows`, the selection's coordinate space
              return (
              <tr key={idx}>
                {vis.map((c, ci) => {
                  const raw = r[c.index];
                  const text = cellText(raw, c.key);
                  const left = frozen.offsetFor(c.key);
                  const cls = [
                    typeof raw === "number" ? "num" : "",
                    left !== undefined ? "pinned-col" : "",
                    left !== undefined && c.key === lastFrozen ? "pinned-edge" : "",
                    sel.isSelected(rowPos, ci) ? "cell-selected" : "",
                  ].filter(Boolean).join(" ");
                  return (
                    <td key={c.key} title={text} className={`cell-clip--wide ${cls}`}
                      style={{ left }}
                      data-row={rowPos} data-col={ci}
                      onMouseDown={(e) => { sel.onCellMouseDown(rowPos, ci, e); sel.focusGrid(); }}
                      onMouseEnter={() => sel.onCellMouseEnter(rowPos, ci)}>
                      {text}
                    </td>
                  );
                })}
                <td className="sticky-note-col">
                  <select className="select" value={draft[idx] ?? NONE} onChange={(e) => setNote(idx, e.target.value)}>
                    {suggestions.map((s) => <option key={s} value={s}>{s}</option>)}
                    {draft[idx] && !suggestions.includes(draft[idx]) && <option value={draft[idx]}>{draft[idx]}</option>}
                  </select>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {sel.statusBar}
      </div>
      {rows.length === 0 && (
        <p className="muted" style={{ margin: "8px 0 0" }}>
          No rows match the current filters — clear a filter above to see rows again.
        </p>
      )}
      <div className="grid-pager">
        <button className="btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Prev</button>
        <span className="muted">Page {page + 1} of {nPages} · {rows.length.toLocaleString()} rows</span>
        <button className="btn" onClick={() => setPage((p) => Math.min(nPages - 1, p + 1))} disabled={page >= nPages - 1}>Next</button>
      </div>
    </div>
      )}
    </FullscreenTable>
  );
}
